import { useMemo, useState } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import {
  FEATURE_REQUEST_SOURCE_LABELS,
  FeatureRequest,
  FeatureRequestStatus,
  ProductFeature,
  getRequestStatusMeta,
} from "../../../api/featureRequests";
import {
  useDeleteRequestsMutation,
  useSetRequestsStatusMutation,
  useUpdateRequestMutation,
} from "../hooks/useFeatureRequestsQueries";
import useUsers from "components/hooks/useUsers";
import { resolveUserName } from "../../Tickets/utils";
import { errorMessage, formatDate } from "../utils";
import NotifyRequestsModal from "./NotifyRequestsModal";
import PickFeatureDialog from "./PickFeatureDialog";
import ConfirmationModal from "../../ConfirmationModal";
import {
  DSTable,
  DSTableBody,
  DSTableCell,
  DSTableContainer,
  DSTableHead,
  DSTablePagination,
  DSTableRow,
} from "../../system-design/Table";
import DSCheckbox from "../../system-design/DSCheckBox";
import DSIconButton from "../../system-design/DSIconButton";
import DSTooltip from "../../system-design/DSTooltip";
import DSChip from "../../system-design/DSChip";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSAlert from "../../system-design/DSAlert";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSErrorButton from "../../system-design/DSErrorButton";

// Reopening a fulfilled request puts it back where its feature stands.
const reopenStatus = (request: FeatureRequest) =>
  request.featureDeployedVersion
    ? FeatureRequestStatus.PotentiallyResolved
    : FeatureRequestStatus.Open;

const RequestsTable = (props: {
  requests: FeatureRequest[];
  features: ProductFeature[];
  showFeature?: boolean;
  emptyText?: string;
}) => {
  const { data: users } = useUsers();
  const setStatus = useSetRequestsStatusMutation();
  const deleteRequests = useDeleteRequestsMutation();
  const updateRequest = useUpdateRequestMutation();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [notifyIds, setNotifyIds] = useState<string[] | null>(null);
  const [deleteIds, setDeleteIds] = useState<string[] | null>(null);
  const [moving, setMoving] = useState<FeatureRequest | null>(null);

  // Selection survives refetches but never points at rows no longer shown
  // (filtered out or deleted).
  const visibleSelected = useMemo(
    () => props.requests.filter((r) => selected.has(r.id)).map((r) => r.id),
    [props.requests, selected],
  );
  const allSelected =
    props.requests.length > 0 &&
    visibleSelected.length === props.requests.length;

  // A filter or delete can shrink the list below the current page.
  const lastPage = Math.max(
    0,
    Math.ceil(props.requests.length / rowsPerPage) - 1,
  );
  const currentPage = Math.min(page, lastPage);
  const pageRows = props.requests.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage,
  );

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const clearSelection = () => setSelected(new Set());

  const actionError = setStatus.error ?? deleteRequests.error;

  if (props.requests.length === 0) {
    return (
      <DSTypography sx={{ textAlign: "center", py: 4 }}>
        {props.emptyText ?? "No requests."}
      </DSTypography>
    );
  }

  return (
    <>
      {visibleSelected.length > 0 && (
        <DSBox
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            p: 1.5,
            mb: 1.5,
            borderRadius: 2,
            bgcolor: "action.selected",
          }}
        >
          <DSTypography sx={{ fontWeight: 700, mr: 1 }}>
            {visibleSelected.length} selected
          </DSTypography>
          <DSPrimaryButton
            size="small"
            onClick={() => setNotifyIds(visibleSelected)}
          >
            Notify customers
          </DSPrimaryButton>
          <DSSecondaryButton
            size="small"
            disabled={setStatus.isPending}
            onClick={() =>
              setStatus.mutate(
                {
                  ids: visibleSelected,
                  status: FeatureRequestStatus.Fulfilled,
                },
                { onSuccess: clearSelection },
              )
            }
          >
            Mark fulfilled
          </DSSecondaryButton>
          <DSErrorButton
            size="small"
            onClick={() => setDeleteIds(visibleSelected)}
          >
            Delete
          </DSErrorButton>
          <DSSecondaryButton size="small" onClick={clearSelection}>
            Clear
          </DSSecondaryButton>
        </DSBox>
      )}

      {actionError && (
        <DSAlert severity="error" sx={{ mb: 1.5 }}>
          {errorMessage(actionError, "Something went wrong.")}
        </DSAlert>
      )}

      <DSTableContainer>
        <DSTable size="small">
          <DSTableHead>
            <DSTableRow>
              <DSTableCell padding="checkbox">
                <DSCheckbox
                  checked={allSelected}
                  indeterminate={visibleSelected.length > 0 && !allSelected}
                  onChange={() =>
                    setSelected(
                      allSelected
                        ? new Set()
                        : new Set(props.requests.map((r) => r.id)),
                    )
                  }
                />
              </DSTableCell>
              <DSTableCell>Date</DSTableCell>
              <DSTableCell>Customer</DSTableCell>
              {props.showFeature && <DSTableCell>Feature</DSTableCell>}
              <DSTableCell>Status</DSTableCell>
              <DSTableCell>Notes</DSTableCell>
              <DSTableCell>Source</DSTableCell>
              <DSTableCell>Notified</DSTableCell>
              <DSTableCell>Added by</DSTableCell>
              <DSTableCell />
            </DSTableRow>
          </DSTableHead>
          <DSTableBody>
            {pageRows.map((request) => {
              const meta = getRequestStatusMeta(request.status);
              const fulfilled =
                request.status === FeatureRequestStatus.Fulfilled;
              return (
                <DSTableRow
                  key={request.id}
                  hover
                  selected={selected.has(request.id)}
                >
                  <DSTableCell padding="checkbox">
                    <DSCheckbox
                      checked={selected.has(request.id)}
                      onChange={() => toggle(request.id)}
                    />
                  </DSTableCell>
                  <DSTableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatDate(request.requestedAt)}
                  </DSTableCell>
                  <DSTableCell sx={{ overflowWrap: "anywhere" }}>
                    <div>{request.customerEmail}</div>
                    {request.customerName && (
                      <DSTypography
                        sx={{ fontSize: 12, color: "text.secondary" }}
                      >
                        {request.customerName}
                      </DSTypography>
                    )}
                  </DSTableCell>
                  {props.showFeature && (
                    <DSTableCell>{request.featureTitle}</DSTableCell>
                  )}
                  <DSTableCell sx={{ whiteSpace: "nowrap" }}>
                    <DSChip
                      label={
                        request.status ===
                          FeatureRequestStatus.PotentiallyResolved &&
                        request.featureDeployedVersion
                          ? `${meta.label} · ${request.featureDeployedVersion}`
                          : meta.label
                      }
                      color={meta.color}
                      size="small"
                      variant="outlined"
                    />
                  </DSTableCell>
                  <DSTableCell
                    sx={{
                      maxWidth: 280,
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {request.notes ?? ""}
                  </DSTableCell>
                  <DSTableCell>
                    {FEATURE_REQUEST_SOURCE_LABELS[request.source] ??
                      request.source}
                  </DSTableCell>
                  <DSTableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatDate(request.notifiedAt)}
                  </DSTableCell>
                  <DSTableCell>
                    {resolveUserName(request.createdBy, users ?? [])}
                  </DSTableCell>
                  <DSTableCell sx={{ whiteSpace: "nowrap" }}>
                    <DSTooltip title={fulfilled ? "Reopen" : "Mark fulfilled"}>
                      <DSIconButton
                        size="small"
                        disabled={setStatus.isPending}
                        onClick={() =>
                          setStatus.mutate({
                            ids: [request.id],
                            status: fulfilled
                              ? reopenStatus(request)
                              : FeatureRequestStatus.Fulfilled,
                          })
                        }
                      >
                        {fulfilled ? (
                          <ReplayIcon fontSize="small" />
                        ) : (
                          <CheckCircleOutlineIcon fontSize="small" />
                        )}
                      </DSIconButton>
                    </DSTooltip>
                    <DSTooltip title="Move to another feature">
                      <DSIconButton
                        size="small"
                        onClick={() => setMoving(request)}
                      >
                        <DriveFileMoveOutlinedIcon fontSize="small" />
                      </DSIconButton>
                    </DSTooltip>
                    <DSTooltip title="Delete request">
                      <DSIconButton
                        size="small"
                        onClick={() => setDeleteIds([request.id])}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </DSIconButton>
                    </DSTooltip>
                  </DSTableCell>
                </DSTableRow>
              );
            })}
          </DSTableBody>
        </DSTable>
      </DSTableContainer>
      <DSTablePagination
        count={props.requests.length}
        page={currentPage}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[25, 50, 100]}
        onPageChange={(_, next) => setPage(next)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(Number(e.target.value));
          setPage(0);
        }}
      />

      <NotifyRequestsModal
        open={notifyIds !== null}
        requestIds={notifyIds ?? []}
        onClose={() => setNotifyIds(null)}
        onSent={clearSelection}
      />
      <ConfirmationModal
        open={deleteIds !== null}
        title="Delete requests"
        message={`Delete ${deleteIds?.length ?? 0} request(s)? This can't be undone.`}
        onClose={() => setDeleteIds(null)}
        onConfirm={() => {
          if (deleteIds) {
            deleteRequests.mutate(deleteIds, {
              onSuccess: () =>
                setSelected((prev) => {
                  const next = new Set(prev);
                  deleteIds.forEach((id) => next.delete(id));
                  return next;
                }),
            });
          }
          setDeleteIds(null);
        }}
      />
      <PickFeatureDialog
        open={moving !== null}
        title="Move request"
        description={
          <>
            Move {moving?.customerEmail}&apos;s request to another feature. It
            starts over there (status follows that feature&apos;s deploy state).
          </>
        }
        confirmLabel="Move"
        features={props.features}
        excludeId={moving?.featureId ?? ""}
        isPending={updateRequest.isPending}
        error={
          updateRequest.isError
            ? errorMessage(updateRequest.error, "Failed to move the request.")
            : null
        }
        onClose={() => {
          setMoving(null);
          updateRequest.reset();
        }}
        onConfirm={(into) =>
          moving &&
          updateRequest.mutate(
            { id: moving.id, input: { featureId: into.id } },
            { onSuccess: () => setMoving(null) },
          )
        }
      />
    </>
  );
};

export default RequestsTable;
